import React, { useState } from "react";
import {
  FileText,
  Sparkles,
  Share2,
  History,
  Users,
  Mail,
  ChevronDown,
  ChevronRight,
  CircleDot,
} from "lucide-react";
import PageHeader from "./PageHeader";

const SECTIONS = [
  {
    id: "ueberblick",
    icon: CircleDot,
    title: "Überblick",
    body: (
      <>
        <p>
          Diese App generiert Stellenanzeigen aus Jotform-Einreichungen, lässt sie per KI feinschleifen
          und mit Partnern abstimmen. Der typische Ablauf:
        </p>
        <ol className="help-steps">
          <li>Partner reicht ein Jotform aus → erscheint als neue Einreichung.</li>
          <li>Du erzeugst aus der Einreichung eine Stellenanzeige (KI-Generierung).</li>
          <li>Du bearbeitest und/oder lässt KI-Überarbeitungen pro Block laufen.</li>
          <li>Du teilst einen Share-Link mit dem Partner zur Abstimmung.</li>
          <li>Kommentare/Antworten direkt auf der Share-Seite. Status wandert von Entwurf bis Abgeschlossen.</li>
        </ol>
      </>
    ),
  },
  {
    id: "anzeigen",
    icon: FileText,
    title: "Stellenanzeige bearbeiten",
    body: (
      <>
        <p>
          Im Anzeige-Editor steht links jede Jotform-Eingabe (Aufgaben, Stärken, Hard Skills, Schwächen,
          Benefits, Disqualifikation), rechts der von der KI generierte Text. Pro Section.
        </p>
        <ul>
          <li><strong>Inline-Edit</strong>: Klick auf einen Block → Bleistift bearbeiten, Enter speichert.</li>
          <li><strong>KI-Überarbeitung</strong>: Sparkles-Icon pro Block öffnet ein Eingabefeld für deine Anweisung. Ohne Anweisung wird neutral umformuliert.</li>
          <li><strong>Drag &amp; Drop</strong>: Aufgaben und Items lassen sich umsortieren.</li>
          <li><strong>Auto-Save</strong>: Änderungen werden automatisch gespeichert (~1,5 s nach letzter Eingabe). Kein Speichern-Button nötig.</li>
        </ul>
      </>
    ),
  },
  {
    id: "ki",
    icon: Sparkles,
    title: "KI-Überarbeiten",
    body: (
      <>
        <p>
          Zwei Varianten:
        </p>
        <ul>
          <li><strong>Pro Block</strong>: Sparkles-Icon am Block — überarbeitet nur diesen einen Abschnitt, optional mit konkreter Anweisung („mehr Du-Form", „kürzer", …).</li>
          <li><strong>Gesamt-Anzeige</strong>: Toolbar-Button „KI-Überarbeiten" — die Agent-Pipeline geht über die ganze Anzeige und korrigiert Probleme. Optional kannst Du eine Gesamt-Anweisung mitgeben.</li>
        </ul>
        <p className="help-note">
          Achtung: KI-Überarbeitung ersetzt den Text. Vorherige Stände findest Du im Änderungsverlauf.
        </p>
      </>
    ),
  },
  {
    id: "share",
    icon: Share2,
    title: "Share-Link &amp; Kommentare",
    body: (
      <>
        <p>
          Über „Share-Link erstellen" friert die Anzeige als Snapshot ein und Du erhältst einen Link
          zum Teilen. Der Partner braucht keinen Login.
        </p>
        <ul>
          <li><strong>Markieren &amp; Kommentieren</strong>: Auf der Share-Seite Text markieren → Kommentar-Button. Antworten in Threads, „Erledigt"-Markierung pro Thread.</li>
          <li><strong>Mehrere Versionen</strong>: Klick auf „Share-Links (X)" → Liste aller Versionen mit Statistik. „Neue Version" macht einen neuen Snapshot — alte Kommentare bleiben.</li>
          <li><strong>Widerrufen</strong>: Versionen können einzeln widerrufen werden, der Link funktioniert dann nicht mehr.</li>
        </ul>
      </>
    ),
  },
  {
    id: "status",
    icon: CircleDot,
    title: "Status",
    body: (
      <>
        <p>Pro Anzeige in der Toolbar wählbar:</p>
        <ul>
          <li><strong>Entwurf</strong> — frisch generiert, noch nicht angefasst.</li>
          <li><strong>In Bearbeitung</strong> — Du arbeitest gerade dran.</li>
          <li><strong>Warten auf Partner</strong> — Share-Link ist beim Partner, Du wartest auf Rückmeldung.</li>
          <li><strong>Abgeschlossen</strong> — fertig freigegeben.</li>
        </ul>
        <p className="help-note">
          Status setzt Du manuell. Er wird in der Partner-Übersicht pro Anzeige als farbige Pill angezeigt.
        </p>
      </>
    ),
  },
  {
    id: "verlauf",
    icon: History,
    title: "Änderungsverlauf",
    body: (
      <>
        <p>
          Im Detail-Editor unten „Änderungsverlauf anzeigen". Pro Bearbeitungs-Session siehst Du gruppiert
          nach Tag und Bearbeiter, was sich geändert hat — als Diff (alt vs. neu), nicht der ganze Snapshot.
        </p>
        <ul>
          <li>5-Minuten-Throttle: schnelle Edits werden zu einer Session zusammengefasst.</li>
          <li>„Auf diesen Stand zurück" stellt eine alte Version wieder her — der aktuelle Stand wird vorher gesichert.</li>
        </ul>
      </>
    ),
  },
  {
    id: "partner",
    icon: Users,
    title: "Partner verwalten",
    body: (
      <>
        <p>
          Im Bereich „Partner" findest Du die Übersicht aller Partner mit Kachel pro Firma. Klick auf eine
          Kachel öffnet alle Stellenanzeigen dieses Partners. Hier:
        </p>
        <ul>
          <li>Neue Partner anlegen, umbenennen oder als „Abgeschlossen" archivieren.</li>
          <li>Pro Anzeige: Bearbeiten · Neu generieren · Löschen, daneben der aktuelle Status.</li>
        </ul>
      </>
    ),
  },
  {
    id: "emails",
    icon: Mail,
    title: "E-Mail-Generator",
    body: (
      <>
        <p>
          Im Bereich „E-Mails" können Antworten auf Bewerbungen / Anfragen mit Hilfe der Agent-Pipeline
          generiert werden. Eingehende Mail einfügen oder hochladen → Tonalität wählen → KI erzeugt eine
          Antwort, die Du vor dem Versenden noch feinschleifen kannst.
        </p>
      </>
    ),
  },
];

export default function HelpPage() {
  const [open, setOpen] = useState(() => Object.fromEntries(SECTIONS.map((s) => [s.id, true])));
  const toggle = (id) => setOpen((p) => ({ ...p, [id]: !p[id] }));
  return (
    <>
      <PageHeader
        eyebrow="Verwaltung"
        title="Anleitung & Hilfe"
        subtitle="Wie die App benutzt wird — Schritt für Schritt."
      />
      <div className="help-toc">
        {SECTIONS.map((s) => (
          <a
            key={s.id}
            href={`#help-${s.id}`}
            className="help-toc-item"
            onClick={(e) => {
              e.preventDefault();
              const el = document.getElementById(`help-${s.id}`);
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
              setOpen((p) => ({ ...p, [s.id]: true }));
            }}
          >
            <s.icon size={14} />
            <span>{s.title}</span>
          </a>
        ))}
      </div>
      <div className="help-sections">
        {SECTIONS.map((s) => (
          <section key={s.id} id={`help-${s.id}`} className={"help-section" + (open[s.id] ? " help-section--open" : "")}>
            <button type="button" className="help-section-head" onClick={() => toggle(s.id)}>
              <s.icon size={16} />
              <h3>{s.title}</h3>
              {open[s.id] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
            {open[s.id] && <div className="help-section-body">{s.body}</div>}
          </section>
        ))}
      </div>
    </>
  );
}
